import { AfterViewChecked, Component, HostListener, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, NavigationStart, ParamMap, Params, Router, Event as RouterEvent } from '@angular/router';
import { DialogValueChangeService, FormsValueChanged, RootDialogComponent } from '@dntlab/share-files-dialog';
import { NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap';
import DevExpress from 'devextreme';
import { DxFileManagerComponent } from 'devextreme-angular';
import CustomFileSystemProvider from 'devextreme/file_management/custom_provider';
import FileSystemItem from 'devextreme/file_management/file_system_item';
import { ContentReadyEvent, FocusedItemChangedEvent } from 'devextreme/ui/file_manager';
import { Observable, Subject, Subscription, filter, fromEvent, map, of, startWith, tap, withLatestFrom } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { promiseWithCatch, safelyDetectChanges, unsubscribeAll } from 'src/app/common/app.helpers';
import AppValues from 'src/app/common/app.values';
import { IDeactivate } from 'src/app/common/guards/save-data-guard/save-data.interface';
import ProjectLinks from 'src/app/common/project.links';
import { DataItem, FileDataItem } from 'src/app/interfaces';
import {
  ContextMenuItem,
  DxManagerFile,
  FileFromManager,
  FileManagerPermissions,
  SelectionManagerItems,
} from 'src/app/interfaces/file.interface';
import { FilePermissions, FileSizeResponse, FoldersResponseInner, SecurityDescriptor } from 'src/app/swagger-api';
import { FileDownload } from '../file-download.abstract';
import { FileManagerService } from '../file-manager.service';
import { ActivityLog, FMActionTypes, FileCategories, FileOwnershipTypes } from '../files-manager.enum';
import { allowedManage, dxFileToFoldersResponseInnerAdapter } from '../files-manager.helpers';
import dxDataGridColumn = DevExpress.ui.dxDataGridColumn;

export type FilePageTabType = 'documents' | 'images' | 'video' | 'audio' | 'activities';

@Component({
  selector: 'app-file-manager',
  templateUrl: './file-manager.component.html',
  providers: [Location, RootDialogComponent],
  styleUrls: ['./file-manager.component.scss'],
})
export class FileManagerComponent extends FileDownload implements OnInit, AfterViewChecked, OnDestroy, IDeactivate {
  public selectedItems: FileSystemItem[] = [];

  public customFileProvider: CustomFileSystemProvider;
  public filesTabActive: {
    allowSharing: boolean;
    allowPublic: boolean;
    allowManaging: boolean;
  } = {
    allowSharing: false,
    allowPublic: false,
    allowManaging: true,
  };
  public canShowUnshareBtn: boolean;
  public activeTab: string;
  public activeFileCategory: ActivityLog | FileCategories;

  public totalStorageSize = 0;
  public unusedStorageSize = 0;
  public progressBarValue = 0;
  public infoValue = 0;
  public warningValue = 0;
  public dangerValue = 0;
  public isCompleted = true;
  public isFormChanged = false;
  public fileManagerHeight = 0;
  public screenWidth: number;

  public fileToDisplay: FileSystemItem;
  public readonly focusedItemChanges$: Subject<FocusedItemChangedEvent> = new Subject();

  public readonly allowedFileExtensions: string[];
  public readonly permissions: FileManagerPermissions;

  public readonly fileOwnershipTypes: typeof FileOwnershipTypes = FileOwnershipTypes;
  public readonly actions: typeof FMActionTypes = FMActionTypes;
  public readonly ACTIVITIES: string = ActivityLog.activities;
  private readonly BYTE_IN_GB: number = AppValues.BYTE_IN_GB;

  private readonly fileListChanges$: Subject<FileSystemItem[]> = new Subject();

  @ViewChild('fileManager', { static: false })
  private fileManager: DxFileManagerComponent;

  @HostListener('window:resize')
  private updateHeight(): void {
    this.screenWidth = window.innerWidth;
    this.fileManagerHeight = window.innerHeight / 1.5;
    safelyDetectChanges(this.changeDetector);
  }

  private subscriptions$: Subscription[] = [];
  private fileInput: HTMLInputElement;

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private service = inject(FileManagerService);
  private sessionService = inject(SessionService);
  private shareDialogService = inject(DialogValueChangeService);

  public constructor() {
    super();
    this.allowedFileExtensions = this.service.allowedFileExtensions;
    this.permissions = this.service.permissions;
  }

  public ngOnInit(): void {
    this.updateHeight();
    this.initDefaultFilter();
    this.initFileProvider();
    this.subscribeOnFilesChanges();
    this.subscribeOnStorageSizeChanges();
    this.subscribeOnDownloadLinkChanges();
    this.subscribeOnRouterChanges();
    this.subscribeToModalWindowChanges();

    this.isFileChangesCompleted().subscribe((isCompleted: boolean) => {
      this.isCompleted = isCompleted;
    });
  }

  public ngAfterViewChecked(): void {
    this.fileInput = document.getElementById('btn-upload-files-input') as HTMLInputElement;

    this.updateFileManagerAttribute();
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  public onContentReady($event: ContentReadyEvent): void {
    this.registerCellEvent($event);
  }

  public isImageTab(): boolean {
    return this.activeFileCategory === FileCategories.images;
  }

  public uploadFiles($event: Event & { target: HTMLInputElement }): void {
    const files = Array.from($event.target.files);

    if (this.service.checkUnusedStorageSize(files, this.unusedStorageSize)) {
      return;
    }

    this.service.uploadFiles(files);
    this.fileInput.value = null;
  }

  public isFileChangesCompleted(): Observable<boolean> {
    return this.service.onFileChanges || of(true);
  }

  public deleteFiles(): void {
    this.service.deleteFiles(dxFileToFoldersResponseInnerAdapter(this.selectedItems));
  }

  public unshareFiles(): void {
    this.service.unshareFiles(dxFileToFoldersResponseInnerAdapter(this.selectedItems));
  }

  public downloadFiles(): void {
    this.service.downloadFiles(this.selectedItems, true);
  }

  public shareFiles(): void {
    this.service.shareFiles(dxFileToFoldersResponseInnerAdapter(this.selectedItems));
  }

  public onMenuItemClick(obj: { itemData: ContextMenuItem }): void {
    if (this.selectedItems && this.selectedItems.length) {
      this.service.onAction({
        item: obj.itemData,
        items: dxFileToFoldersResponseInnerAdapter(this.selectedItems),
        selected: this.selectedItems,
      });
    }
  }

  public onSelectionChanged(selectedItems: SelectionManagerItems): void {
    this.updateUnshareBtnVisibility(selectedItems);

    this.selectedItems = selectedItems.selectedItems;
    selectedItems.selectedItems.forEach((item: FileSystemItem) => this.checkSecurityDescriptor(item));
    safelyDetectChanges(this.changeDetector);
  }

  public updateUnshareBtnVisibility(selectedItems: SelectionManagerItems): void {
    this.canShowUnshareBtn =
      selectedItems.selectedItems.length > 0
        ? selectedItems.selectedItems.every((item: FileSystemItem) => {
            return (
              !(item.dataItem as FileDataItem).dataItem.item.entity.is_public &&
              (item.dataItem as FileDataItem).dataItem.item.security.some((elem: SecurityDescriptor) => {
                return elem.user_id !== this.sessionService.userId && elem.permission === FilePermissions.Owner;
              })
            );
          })
        : false;
  }

  public checkSecurityDescriptor(item: FileSystemItem): void {
    this.filesTabActive.allowManaging = allowedManage(
      this.sessionService.userId,
      (item.dataItem as FileDataItem).dataItem,
    );
  }

  public openFile(obj: DxManagerFile): void {
    this.service.navigateTo((obj.file.dataItem as FileDataItem).dataItem.item as FoldersResponseInner);
  }

  public onSelectedItems(items: FileSystemItem[]): void {
    this.selectedItems = items;
  }

  public onTabClick($event: NgbNavChangeEvent<string>): void {
    promiseWithCatch(
      this.router.navigate([`${ProjectLinks.LINK_FILES}/${$event.nextId}`], {
        queryParams: { type: this.activeFileCategory },
      }),
    );
    this.activeTab = $event.nextId;
    this.service.currentOwnershipType = $event.nextId;
    this.changeFilesTabActive($event);
    this.refreshFileManager(true);
    this.changeDetector.detectChanges();
  }

  public changeFilesTabActive($event: NgbNavChangeEvent<string>): void {
    this.filesTabActive = {
      allowSharing: $event.nextId === FileOwnershipTypes.shared,
      allowPublic: $event.nextId === FileOwnershipTypes.public,
      allowManaging: $event.nextId === FileOwnershipTypes.manager,
    };
  }

  public displayPopup(fileFromManager: FileFromManager): void {
    this.fileToDisplay = fileFromManager.file;
    this.generateProtectedUrlAndShowPopup();
  }

  public generateProtectedUrlAndShowPopup(): void {
    this.titleToDisplay = this.fileToDisplay.name;
    this.popupPublicURL = (this.fileToDisplay.dataItem as FileDataItem).dataItem.item.entity.public_url;

    if (!this.popupPublicURL) {
      this.subscriptions$.push(
        this.service.downloadFiles([this.fileToDisplay], false).subscribe((url: string) => {
          this.protectedPublicURL = this.generateProtectedUrl(url);

          this.openingFile(url, (this.fileToDisplay.dataItem as FileDataItem).dataItem.item.entity.content_type);
        }),
      );
    } else {
      this.protectedPublicURL = this.generateProtectedUrl(this.popupPublicURL);
      this.openingFile(
        this.popupPublicURL,
        (this.fileToDisplay.dataItem as FileDataItem).dataItem.item.entity.content_type,
      );
    }
  }

  public getCurrentRoute(activeTabId: string): string {
    return `/${ProjectLinks.LINK_FILES}/${activeTabId}`;
  }

  public setDefaultValueForModal(): void {
    this.shareDialogService.updateValue({ usersForm: false, searchForm: false });
  }

  public customizeDetailColumns(e: dxDataGridColumn[]): dxDataGridColumn[] {
    const columns: dxDataGridColumn[] = e.slice();
    columns.find((c: dxDataGridColumn) => c.dataField === 'dateModified').format = AppValues.DATE_FORMAT_SHORT;
    return columns;
  }

  private initDefaultFilter(): void {
    this.subscriptions$.push(
      this.route.paramMap.subscribe((params: ParamMap) => {
        if (params.has('tab')) {
          this.activeTab = params.get('tab');
          this.service.setTabType(params.get('tab'));
          this.filesTabActive = {
            allowSharing: params.get('tab') === FileOwnershipTypes.shared,
            allowPublic: params.get('tab') === FileOwnershipTypes.public,
            allowManaging: params.get('tab') === FileOwnershipTypes.manager,
          };
        }
      }),
      this.route.queryParams.subscribe((params: Params) => {
        this.activeFileCategory = params['type'] as ActivityLog | FileCategories;
        (params['type'] as ActivityLog | FileCategories) !== ActivityLog.activities
          ? this.service.setCategory(params['type'] as string)
          : (this.selectedItems = []);
      }),
    );
  }

  private subscribeOnFilesChanges(): void {
    this.subscriptions$.push(
      this.service.onFileChanges.subscribe((changed: boolean) => {
        this.refreshFileManager(changed);
      }),
    );
  }

  private subscribeOnRouterChanges(): void {
    this.subscriptions$.push(
      this.router.events.subscribe((event: RouterEvent) => {
        if (event instanceof NavigationStart && this.popupVisible) {
          this.hidePopup();
          this.popupVisible = false;
        } else if (event instanceof NavigationStart) {
          this.selectedItems = [];
          this.canShowUnshareBtn = false;
        }
      }),
    );
  }

  private subscribeOnDownloadLinkChanges(): void {
    this.subscriptions$.push(
      this.service.onDownloadLinkReceived.subscribe((links: { url: string; item: FileSystemItem }[]) => {
        this.protectedPublicURL = this.generateProtectedUrl(links[0].url);
        this.downloadFile(links[0].url);
        safelyDetectChanges(this.changeDetector);
      }),
    );
  }

  private subscribeOnStorageSizeChanges(): void {
    this.subscriptions$.push(
      this.service.onStorageSizeChanged.subscribe((response: FileSizeResponse) => {
        this.totalStorageSize = response.total_size / this.BYTE_IN_GB;
        this.unusedStorageSize = +((response.total_size - response.used_size) / this.BYTE_IN_GB).toFixed(2);

        this.calculateProgressBarStyleValues();

        this.progressBarValue = (response.used_size / response.total_size) * 100;
        this.changeDetector.detectChanges();
      }),
    );
  }

  /**
   * @description Calculates values for progress bar customization.
   */
  private calculateProgressBarStyleValues(): void {
    this.infoValue = this.totalStorageSize * 25;
    this.warningValue = this.totalStorageSize * 50;
    this.dangerValue = this.totalStorageSize * 75;
  }

  // TODO: cover with tests
  private refreshFileManager(changed: boolean): void {
    if (this.fileManager && this.fileManager.instance) {
      this.fileManager.instance.option('selectedItemKeys', []);

      this.isCompleted = changed;
      changed ? promiseWithCatch(this.fileManager.instance.refresh()) : this.fileManager.instance.repaint();
    }
  }

  private updateFileManagerAttribute(): void {
    if (this.activeFileCategory !== this.ACTIVITIES) {
      // fix register mouse events listeners for Safari(iOS)
      const ROW_SELECTOR = 'tr.dx-data-row';
      this.fileManager.instance
        .element()
        .querySelectorAll(ROW_SELECTOR)
        .forEach((row: Element) => {
          row.setAttribute('role', 'button');
          row.setAttribute('tabindex', '0');
        });

      // add data-test-id for each cell
      const CELL_SELECTOR = 'td';
      this.fileManager.instance
        .element()
        .querySelectorAll(CELL_SELECTOR)
        .forEach((cell: Element) => {
          const rowIndex: string = cell.parentElement.getAttribute('aria-rowindex');
          const colIndex: string = cell.getAttribute('aria-colindex');
          let colHeaderLabel: string = cell.getAttribute('aria-label');
          colHeaderLabel ? (colHeaderLabel = colHeaderLabel.replace(/ /g, '-').toLowerCase()) : colHeaderLabel;

          colHeaderLabel
            ? cell.setAttribute('data-test-id', colHeaderLabel + '-' + colIndex)
            : cell.setAttribute('data-test-id', 'row-' + rowIndex + '-cell-' + colIndex);
        });
    }
  }

  private initFileProvider(): void {
    this.customFileProvider = new CustomFileSystemProvider({
      getItems: (parentDirectory: FileSystemItem) => {
        return this.service
          .getItemsByPath(this.activeFileCategory, parentDirectory.key)
          .then((items: FileSystemItem[]) => {
            this.fileListChanges$.next(items);
            return items;
          });
      },
    });
  }

  private subscribeToModalWindowChanges(): void {
    this.subscriptions$.push(
      this.shareDialogService.valueChanged.subscribe((value: FormsValueChanged) => {
        this.isFormChanged = value.searchForm || value.usersForm;
      }),
    );
  }

  private registerCellEvent(event: ContentReadyEvent): void {
    type CellEvent = Event & { target: HTMLTableCellElement };

    const filesChanged$ = this.fileListChanges$.pipe(startWith([]));

    const fileSelected$ = this.focusedItemChanges$.pipe(
      map((eventData: FocusedItemChangedEvent) => eventData.item),
      filter((item: FileSystemItem): item is FileSystemItem => Boolean(item)),
    );

    const cellClicked$ = fromEvent<CellEvent>(event.element, 'click').pipe(
      tap((e: CellEvent) => e.stopImmediatePropagation()),
    );

    this.subscriptions$.push(
      cellClicked$
        .pipe(
          withLatestFrom(fileSelected$, filesChanged$),
          tap(([cellEvent, selectedFile, fileList]: [CellEvent, FileSystemItem, FileSystemItem[]]) => {
            const dataItem: DataItem | undefined = fileList.find(
              (item: FileSystemItem) => item.key === selectedFile.key,
            )?.dataItem as DataItem;

            if (
              cellEvent.target.textContent === dataItem?.members &&
              allowedManage(this.sessionService.userId, dataItem)
            ) {
              this.service.shareFiles([dataItem.item] as FoldersResponseInner[]);
            }
          }),
        )
        .subscribe(),
    );
  }
}
